import { Button, List, LoadIndicator, RadioGroup, ScrollView, TextBox } from "devextreme-react";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { setFeaturesViewerAllByRole, updateFeaturePermissionsByRole } from "../../../../actions";
import useSWR from 'swr'
import { fetcher } from "../../../../actions";
import './FeaturesByRole.css';
const _ = require("lodash");



function GroupTemplate(item) {
    return (
        <div className="feature-group-header">
            <i className="dx-icon-folder" style={{ marginRight: 8 }}></i>
            <strong>{item.key}</strong>
        </div>
    );
}

const groupArray = (data) => {
    let grouped_data = _.groupBy(data, "typeFeature")
    var result = Object.keys(grouped_data).map(x => {
        return { "key": x, "items": grouped_data[x] }
    });
    return result;
}

export const FeaturesByRole = (props) => {
    const { roleId } = props;
    const [controlState, setControlState] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const { data, isLoading } = useSWR(`/api/RoleFeatures/reportRolesFeaturesPermissions?idRole=${roleId}`, fetcher);

    const dispatch = useDispatch();
    
    const setPermissionValue = useCallback((e, item) => {
        // update the database here
        dispatch(updateFeaturePermissionsByRole(item.idRole, item.idFeature, e));

        let groupIndex = controlState.findIndex(x => x.items.find(x => x.idFeature === item.idFeature));
        let items = [...controlState[groupIndex].items];
        let objectIndex = items.findIndex(x => x.idFeature === item.idFeature);
        let newItem = { ...item };
        newItem.permission = e;
        items[objectIndex] = newItem;
        const result = [...controlState];
        result[groupIndex] = { ...controlState[groupIndex], "items": items };
        setControlState([...result]);
    }, [controlState, dispatch]);

    const setViewerAll = useCallback((roleId) => {
        dispatch(setFeaturesViewerAllByRole(roleId));
    }, [dispatch]);

    const getPermissionIcon = useCallback((permission) => {
        if (permission === 0) return 'dx-icon-close';
        if (permission === 1) return 'dx-icon-eyeopen';
        return 'dx-icon-edit';
    }, []);

    const renderItem = useCallback((item) => {
        const writePermissions = [
            { id: 0, text: 'None' },
            { id: 1, text: 'Viewer' },
            { id: 2, text: 'Editor' }
        ];
        const readPermissions = [
            { id: 0, text: 'None' },
            { id: 1, text: 'Viewer' }
        ];
        let permissions = writePermissions;
        if (item.editable === 0) {
            permissions = readPermissions;
        }
        
        const permissionClass = item.permission === 0 ? 'badge-default' : 
                                item.permission === 1 ? 'badge-info' : 'badge-success';
        const permissionText = item.permission === 0 ? 'No Access' : 
                               item.permission === 1 ? 'Viewer' : 'Editor';
        
        return (
            <div className="feature-item">
                <div className="feature-item-row">
                    <div className="feature-item-left">
                        <div className="feature-info">
                            <i className="dx-icon-favorites" style={{ fontSize: 16, marginRight: 8 }}></i>
                            <span className="feature-name">{item.nameFeature}</span>
                            <span className={`permission-badge ${permissionClass}`}>
                                <i className={getPermissionIcon(item.permission)} style={{ fontSize: 12, marginRight: 4 }}></i>
                                {permissionText}
                            </span>
                            {item.editable === 0 && (
                                <span className="badge-readonly">Read Only</span>
                            )}
                        </div>
                    </div>
                    <div className="feature-item-right">
                        <RadioGroup 
                            id="radio-group-with-selection" 
                            items={permissions} 
                            value={item.permission}
                            valueExpr="id" 
                            displayExpr="text" 
                            onValueChange={(e) => setPermissionValue(e, item)}
                            layout="horizontal"
                        />
                    </div>
                </div>
            </div>
        );
    }, [getPermissionIcon, setPermissionValue]);

    const grouped = useMemo(() => groupArray(data), [data]);
    
    useEffect(() => {
        setControlState(grouped);
    }, [grouped]);
    
    // Filter data based on search term
    const filteredData = useMemo(() => {
        if (!controlState || controlState.length === 0) return [];
        if (!searchTerm) return controlState;
        
        return controlState.map(group => ({
            ...group,
            items: group.items.filter(item => 
                item.nameFeature.toLowerCase().includes(searchTerm.toLowerCase())
            )
        })).filter(group => group.items.length > 0);
    }, [controlState, searchTerm]);
    
    // Calculate statistics
    const statistics = useMemo(() => {
        if (!data) return { totalFeatures: 0, editorCount: 0, viewerCount: 0, noAccessCount: 0 };
        return {
            totalFeatures: data.length,
            editorCount: data.filter(i => i.permission === 2).length,
            viewerCount: data.filter(i => i.permission === 1).length,
            noAccessCount: data.filter(i => i.permission === 0).length
        };
    }, [data]);


    if (isLoading) {
        return (
            <div className="loading-container">
                <LoadIndicator visible={true} />
                <div className="loading-text">Loading features...</div>
            </div>
        );
    }

    return (
        <div id="FeaturesByRoleContainer" className="features-container">
            {/* Header with stats */}
            <div className="stats-row">
                <span className="stat-badge badge-primary">
                    <i className="dx-icon-favorites" />
                    {statistics.totalFeatures} Features
                </span>
                <span className="stat-badge badge-success">
                    <i className="dx-icon-edit" />
                    {statistics.editorCount} Editor
                </span>
                <span className="stat-badge badge-info">
                    <i className="dx-icon-eyeopen" />
                    {statistics.viewerCount} Viewer
                </span>
                <span className="stat-badge badge-default">
                    <i className="dx-icon-close" />
                    {statistics.noAccessCount} No Access
                </span>
            </div>

            {/* Description */}
            <div className="info-alert">
                <div className="info-alert-content">
                    <strong>Define access to predefined menu and features depending on the selected role:</strong>
                    <div className="info-list">
                        • <strong>None:</strong> The role has no access to the feature<br/>
                        • <strong>Viewer:</strong> Can access but not edit anything<br/>
                        • <strong>Editor:</strong> Can access and edit
                    </div>
                </div>
            </div>

            {/* Search and bulk actions */}
            <div className="actions-row">
                <div className="search-box">
                    <TextBox
                        placeholder="Search features..."
                        value={searchTerm}
                        onValueChanged={(e) => setSearchTerm(e.value)}
                        mode="search"
                        width="100%"
                    />
                </div>
                <Button 
                    text="Set All to Viewer"
                    icon="eyeopen"
                    type="default"
                    onClick={() => setViewerAll(roleId)}
                />
            </div>

            {/* Features list */}
            <div className="features-list-container">
                <ScrollView width='100%' height='100%'>
                    <div className="list-container">
                        <List
                            dataSource={filteredData}
                            height="100%"
                            grouped={true}
                            collapsibleGroups={true}
                            groupRender={GroupTemplate}
                            itemRender={renderItem}
                            repaintChangesOnly={true}
                        />
                    </div>
                </ScrollView>
            </div>
        </div>
    );
}


export default FeaturesByRole; 
